import React, { useState, useEffect } from 'react';
import { FiX as X, FiAlertTriangle as AlertTriangle } from 'react-icons/fi';

interface MetricOption {
  value: string;
  label: string;
  defaultThreshold: number;
}

interface ConditionOption {
  value: string;
  label: string;
}

interface SeverityOption {
  value: string;
  label: string;
  color: string;
}

const METRIC_OPTIONS: MetricOption[] = [
  { value: 'heap_percent', label: 'Heap Usage (%)', defaultThreshold: 80 },
  { value: 'cpu_usage_percent', label: 'CPU Usage (%)', defaultThreshold: 90 },
  { value: 'thread_count', label: 'Thread Count', defaultThreshold: 100 },
  { value: 'thread_blocked', label: 'Blocked Threads', defaultThreshold: 10 },
  { value: 'rss', label: 'RSS Memory (bytes)', defaultThreshold: 1073741824 },
];

const CONDITION_OPTIONS: ConditionOption[] = [
  { value: '>', label: 'Greater than (>)' },
  { value: '>=', label: 'Greater than or equal (>=)' },
  { value: '<', label: 'Less than (<)' },
  { value: '<=', label: 'Less than or equal (<=)' },
  { value: '==', label: 'Equals (==)' },
];

const SEVERITY_OPTIONS: SeverityOption[] = [
  { value: 'warning', label: 'Warning', color: 'text-yellow-600' },
  { value: 'critical', label: 'Critical', color: 'text-red-600' },
];

export interface AlertRule {
  id?: string;
  name: string;
  description: string;
  metric_type: string;
  condition: string;
  threshold: number;
  severity: string;
  agent_name_pattern: string | null;
  enabled: boolean;
  cooldown_seconds: number;
}

interface FormData {
  name: string;
  description: string;
  metric_type: string;
  condition: string;
  threshold: number | string;
  severity: string;
  agent_name_pattern: string;
  enabled: boolean;
  cooldown_seconds: number | string;
}

interface SaveResult {
  success: boolean;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave?: (payload: AlertRule, id?: string) => Promise<SaveResult | undefined>;
  editingRule?: AlertRule | null;
  saving?: boolean;
}

const APMAlertRuleModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onSave,
  editingRule = null,
  saving = false,
}) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    metric_type: 'heap_percent',
    condition: '>',
    threshold: 80,
    severity: 'warning',
    agent_name_pattern: '',
    enabled: true,
    cooldown_seconds: 300,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (editingRule) {
      setFormData({
        name: editingRule.name ?? '',
        description: editingRule.description ?? '',
        metric_type: editingRule.metric_type ?? 'heap_percent',
        condition: editingRule.condition ?? '>',
        threshold: editingRule.threshold ?? 80,
        severity: editingRule.severity ?? 'warning',
        agent_name_pattern: editingRule.agent_name_pattern ?? '',
        enabled: editingRule.enabled ?? true,
        cooldown_seconds: editingRule.cooldown_seconds ?? 300,
      });
    } else {
      setFormData({
        name: '',
        description: '',
        metric_type: 'heap_percent',
        condition: '>',
        threshold: 80,
        severity: 'warning',
        agent_name_pattern: '',
        enabled: true,
        cooldown_seconds: 300,
      });
    }
    setErrors({});
  }, [editingRule, isOpen]);

  const handleMetricChange = (metric: string): void => {
    const option = METRIC_OPTIONS.find(m => m.value === metric);
    setFormData(prev => ({
      ...prev,
      metric_type: metric,
      threshold: option?.defaultThreshold ?? prev.threshold,
    }));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!String(formData.name).trim()) {
      newErrors.name = 'Name is required';
    }
    if (formData.threshold == null || formData.threshold === '') {
      newErrors.threshold = 'Threshold is required';
    }
    if (Number(formData.cooldown_seconds) < 0) {
      newErrors.cooldown_seconds = 'Cooldown must be positive';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!validate()) return;

    const payload: AlertRule = {
      ...formData,
      threshold: Number.parseFloat(String(formData.threshold)),
      cooldown_seconds: Number.parseInt(String(formData.cooldown_seconds), 10),
      agent_name_pattern: formData.agent_name_pattern || null,
    };

    if (onSave) {
      const result = await onSave(payload, editingRule?.id);
      if (result?.success) {
        onClose();
      }
    }
  };

  if (!isOpen) return null;

  let submitButtonLabel: string;
  if (saving) {
    submitButtonLabel = 'Saving...';
  } else if (editingRule) {
    submitButtonLabel = 'Update Rule';
  } else {
    submitButtonLabel = 'Create Rule';
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75"
          onClick={onClose} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClose(); }} role="presentation"
        />
        {/* Modal */}
        <div className="inline-block w-full max-w-lg p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-lg">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {editingRule ? 'Edit Alert Rule' : 'Create Alert Rule'}
            </h3>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name */}
            <div>
              <label htmlFor="apm-rule-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Rule Name *
              </label>
              <input
                                                                                  id="apm-rule-name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                  focus:outline-none focus:ring-2 focus:ring-blue-500
                  ${errors.name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                placeholder="e.g., High Memory Warning"
              />
              {errors.name && (
                <p className="mt-1 text-xs text-red-500">{errors.name}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label htmlFor="apm-alert-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <textarea
                                                                                  id="apm-alert-description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                  bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                  focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Optional description"
              />
            </div>

            {/* Metric Type */}
            <div>
              <label htmlFor="apm-metric-type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Metric Type *
              </label>
              <select
                                                                                  id="apm-metric-type"
                value={formData.metric_type}
                onChange={(e) => handleMetricChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                  bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                  focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {METRIC_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Condition and Threshold */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="apm-condition" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Condition *
                </label>
                <select
                                                                                  id="apm-condition"
                  value={formData.condition}
                  onChange={(e) => setFormData(prev => ({ ...prev, condition: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                    bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                    focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {CONDITION_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="apm-alert-threshold" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Threshold *
                </label>
                <input
                                                                                  id="apm-alert-threshold"
                  type="number"
                  value={formData.threshold}
                  onChange={(e) => setFormData(prev => ({ ...prev, threshold: e.target.value }))}
                  step="any"
                  className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                    focus:outline-none focus:ring-2 focus:ring-blue-500
                    ${errors.threshold ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                />
                {errors.threshold && (
                  <p className="mt-1 text-xs text-red-500">{errors.threshold}</p>
                )}
              </div>
            </div>

            {/* Severity */}
            <div>
              <label htmlFor="apm-severity" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Severity *
              </label>
              <div className="flex gap-4">
                {SEVERITY_OPTIONS.map(opt => (
                  <label
                    key={opt.value}
                    className={`flex items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer transition-colors
                      ${formData.severity === opt.value
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                  >
                    <input
                                                                                  id="apm-severity"
                      type="radio"
                      name="severity"
                      value={opt.value}
                      checked={formData.severity === opt.value}
                      onChange={(e) => setFormData(prev => ({ ...prev, severity: e.target.value }))}
                      className="sr-only"
                    />
                    <AlertTriangle className={`w-4 h-4 ${opt.color}`} />
                    <span className="text-sm text-gray-900 dark:text-white">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Agent Filter */}
            <div>
              <label htmlFor="apm-agent-pattern" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Agent Name Pattern
              </label>
              <input
                                                                                  id="apm-agent-pattern"
                type="text"
                value={formData.agent_name_pattern}
                onChange={(e) => setFormData(prev => ({ ...prev, agent_name_pattern: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                  bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                  focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Leave empty for all agents (supports regex)"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Filter by agent name. Supports regex patterns.
              </p>
            </div>

            {/* Cooldown */}
            <div>
              <label htmlFor="apm-alert-cooldown" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Cooldown (seconds)
              </label>
              <input
                                                                                  id="apm-alert-cooldown"
                type="number"
                value={formData.cooldown_seconds}
                onChange={(e) => setFormData(prev => ({ ...prev, cooldown_seconds: e.target.value }))}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                  bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                  focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Minimum time between alerts for this rule (default: 300s)
              </p>
            </div>

            {/* Enabled */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="enabled"
                checked={formData.enabled}
                onChange={(e) => setFormData(prev => ({ ...prev, enabled: e.target.checked }))}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="enabled" className="text-sm text-gray-700 dark:text-gray-300">
                Enable this rule
              </label>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300
                  bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600
                  rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg
                  hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitButtonLabel}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default APMAlertRuleModal;
